/**
 * The Overview route (BON-534).
 *
 * One page for every role, driven by which region the address names.
 * `/admin/overview` is every region; `/admin/overview/<ancestors>/<region>` is one region at
 * any depth, with the address spelling out the whole path so it doubles as the
 * breadcrumb and cannot be ambiguous about which sub-region is meant.
 *
 * Staying put across a refresh falls out of that — the scope is the URL, so
 * nothing is stored anywhere.
 */
import { Navigate, useParams } from 'react-router-dom';

import { DashboardsPage } from '../../DashboardsPage';

import { AllRegionsOverview } from './AllRegionsOverview';
import { useRegionOverview } from './overview-api';
import { OverviewHeader } from './OverviewHeader';
import { OverviewScopeNote } from './OverviewScopeNote';
import { RegionOverview } from './RegionOverview';
import { useOverviewHomeRedirect } from './use-overview-home-redirect';

const NOTE: React.CSSProperties = {
  padding: 14,
  borderRadius: 10,
  background: 'var(--nb-bg)',
  border: '1px dashed var(--nb-border)',
  fontSize: 12.5,
  color: 'var(--nb-subtle)',
};

/** The splat, as path segments. A trailing or doubled slash addresses the same
 *  region, matching how the server reads it. */
function usePathSegments(): string[] {
  const params = useParams();
  return String(params['*'] ?? '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
}

export function OverviewDashboard() {
  const path = usePathSegments();
  const isAllRegions = path.length === 0;
  const { data, isLoading, isError } = useRegionOverview(path);
  const home = useOverviewHomeRedirect();

  if (isAllRegions && home.pending) {
    return (
      <DashboardsPage header={<OverviewHeader />}>
        <p style={NOTE}>Loading…</p>
      </DashboardsPage>
    );
  }
  // `replace`, so Back from the viewer's region does not land on a root that
  // would send them straight forward again.
  if (isAllRegions && home.to) return <Navigate to={home.to} replace />;

  if (isAllRegions) {
    return (
      <DashboardsPage header={<OverviewHeader />}>
        <OverviewScopeNote />
        <AllRegionsOverview />
      </DashboardsPage>
    );
  }

  // The header needs the region's own name and ancestors, which only arrive
  // with the response — so the region branch renders its header from the data
  // rather than from the URL, and falls back to the all-regions header while
  // there is nothing to name.
  if (isError) {
    return (
      <DashboardsPage header={<OverviewHeader />}>
        <p style={NOTE}>That region could not be found.</p>
      </DashboardsPage>
    );
  }
  if (isLoading || !data) {
    return (
      <DashboardsPage header={<OverviewHeader />}>
        <p style={NOTE}>Loading…</p>
      </DashboardsPage>
    );
  }

  return (
    <DashboardsPage header={<OverviewHeader region={data.region} ancestors={data.ancestors} />}>
      <OverviewScopeNote regionId={data.region.id} />
      <RegionOverview overview={data} />
    </DashboardsPage>
  );
}
